import { Typography } from '@mui/material';
import { Icon, TIcons } from '../Icon';
import MuiButton, { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { ComponentProps, MouseEventHandler } from 'react';
import palette from 'theme/palette';

export interface IButtonProps extends LoadingButtonProps {
  color?: 'primary' | 'secondary' | 'error' | LoadingButtonProps['color'];
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  type?: 'submit' | 'reset';
  startIcon?: TIcons;
  endIcon?: TIcons;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
  loading?: boolean;
  dataTestId?: string;
  startIconWidth?: number;
  startIconHeight?: number;
  endIconWidth?: number;
  endIconHeight?: number;
  iconColor?: ComponentProps<typeof Icon>['color'];
}

export const Button = ({
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  variant = 'contained',
  startIcon,
  endIcon,
  onClick,
  children,
  loading,
  type,
  startIconWidth = 20,
  startIconHeight = 20,
  endIconWidth = 20,
  endIconHeight = 20,
  dataTestId,
  iconColor,
  sx = {},
  ...rest
}: IButtonProps) => {
  const typographyVariant: 'buttonSm' | 'buttonMd' | 'buttonLg' =
    size === 'small' ? 'buttonSm' : size === 'medium' ? 'buttonMd' : 'buttonLg';

  const backgroundColor =
    color === 'secondary' ? palette.button.secondary : color === 'primary' ? palette.button.primary : 'initial';
  const hoverColor =
    color === 'secondary'
      ? palette.button.secondaryHover
      : color === 'primary'
      ? palette.button.primaryHover
      : 'initial';

  return (
    <MuiButton
      color={color}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      variant={variant}
      disableElevation={true}
      onClick={onClick}
      loading={loading}
      type={type}
      startIcon={
        startIcon && <Icon name={startIcon} width={startIconWidth} height={startIconHeight} color={iconColor} />
      }
      endIcon={endIcon && <Icon name={endIcon} width={endIconWidth} height={endIconHeight} color={iconColor} />}
      data-testid={dataTestId}
      sx={{
        ...(variant !== 'text' && {
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: hoverColor
          }
        }),
        ...sx
      }}
      {...rest}
    >
      <Typography variant={typographyVariant}>{children}</Typography>
    </MuiButton>
  );
};

export default Button;
